/**
 * @fileoverview Ops-console sign in. Email + password → a platform session
 * cookie; on success routes into the /ops console. Built on the Studio Quiet
 * token layer (the older org/instructor auth screens predate it).
 */

import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Card, Button, Icon } from "@/components/studio/primitives";
import opsApi, { getOpsErrorMessage } from "@/lib/opsApi";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const field =
  "mt-1 w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] px-4 py-[11px] font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-yellow)]";
const label = "block font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]";

export default function OpsLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim()) || !password) {
      setError("Enter your email and password");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await opsApi.post("/ops/auth/login", { email: email.trim(), password });
      navigate("/ops");
    } catch (err) {
      toast.error(getOpsErrorMessage(err, "Invalid email or password"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="studio flex min-h-screen items-center justify-center bg-[var(--studio-base)] p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mb-6 flex items-center justify-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)]">
              <Icon name="shield" size={20} strokeWidth={2} className="text-[var(--accent-yellow-text)]" />
            </div>
            <span className="font-serif text-lg font-semibold tracking-tight text-[var(--text-primary)]">Hans Ops</span>
          </div>
          <h1 className="mb-2 font-serif text-2xl font-light text-[var(--text-primary)]">Operations sign in</h1>
          <p className="font-sans text-sm text-[var(--text-tertiary)]">Runtime flags and the platform audit ledger</p>
        </div>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <Card className="space-y-4">
            <div>
              <label className={label} htmlFor="ops-login-email">
                Email
              </label>
              <input
                id="ops-login-email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError("");
                }}
                required
                className={field}
                placeholder="you@kalver.ai"
                autoComplete="email"
                data-testid="ops-login-email"
              />
            </div>
            <div>
              <label className={label} htmlFor="ops-login-password">
                Password
              </label>
              <input
                id="ops-login-password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError("");
                }}
                required
                className={field}
                placeholder="••••••••"
                autoComplete="current-password"
                data-testid="ops-login-password"
              />
            </div>
            {error && (
              <p className="font-sans text-xs text-[var(--state-danger)]" data-testid="ops-login-error">
                {error}
              </p>
            )}
          </Card>

          <Button
            type="submit"
            disabled={submitting}
            fullWidth
            icon={submitting ? "loader" : "lock"}
            data-testid="ops-login-submit"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/ops/forgot-password"
            className="font-sans text-xs text-[var(--text-tertiary)] transition-colors hover:text-[var(--text-primary)]"
          >
            Forgot your password?
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
